import React, { useCallback, useRef, useState } from 'react';
import {
    Accordion,
    Alert,
    Badge,
    Button,
    Card,
    Form,
    Modal,
    OverlayTrigger,
    Spinner,
    Tooltip,
} from 'react-bootstrap';
import {
    AlertTriangle,
    Copy,
    FileSpreadsheet,
    FileText,
    HelpCircle,
    Mail,
    Upload,
    X,
} from 'lucide-react';
import { FiBookOpen, FiDownload } from 'react-icons/fi';
import { importTripsPoliciesStandard } from '../../../services/api';
import { handleApiError } from '../../../utils/errorHandler';
import {
    STANDARD_IMPORT_COLUMNS,
    STANDARD_IMPORT_OPTIONAL_TIME_COLUMNS,
} from '../../../constants/standardImportColumns';
import { downloadStandardImportTemplate } from '../../../utils/standardImportTemplate';
import { getEmailListItemLabel } from '../../../constants/emailListItems';
import {
    buildTripsPoliciesStandardResultMessage,
    formatSubscriptionEmailReason,
    getRejectedUnknownActivities,
    getRejectedUnknownCompanyNames,
    getRejectedStandardScopeCompanyNames,
    getStandardScopeRejections,
    getStandardValidationErrors,
    getPartnerImportErrors,
    getPartnerErrors,
    getSkippedReservations,
    getSubscriptionEmails,
    hasActivityNotFoundRejections,
    hasActivityTypeRequiresExcelTimesValidationErrors,
    hasActivityTypeSameDayValidationErrors,
    hasMissingCompanyNameRejections,
    hasPartnerImportErrors,
    hasPartnerErrors,
    hasPartnerNotFoundRejections,
    hasSkippedReservations,
    hasStandardScopeRejections,
    hasValidationErrors,
    isTripsPoliciesStandardFailure,
    parseStandardImportStats,
} from '../../../utils/tripsPoliciesImportResult';
import './standardImport.css';

const getResponseDetailError = (response) => {
    const detail = response?.detail;
    if (typeof detail === 'string' && detail.trim()) return detail.trim();
    if (Array.isArray(detail) && detail.length > 0) {
        const messages = detail
            .map((item) => (typeof item === 'string' ? item : item?.msg || item?.message))
            .filter(Boolean);
        if (messages.length > 0) return messages.join(' ; ');
    }
    return null;
};

const PAGE_PREFIX = 'pages.adminTripsPoliciesImport';
const STORAGE_KEY = 'importPoliciesStandardResult';

export const STANDARD_REQUIRED_COLUMNS = STANDARD_IMPORT_COLUMNS;

const LabelWithTooltip = ({ label, tooltip, htmlFor, required = false }) => (
    <div className="si-field-label">
        <Form.Label htmlFor={htmlFor} className="mb-0 fw-semibold">
            {label}
            {required && <span className="text-danger ms-1" aria-hidden>*</span>}
        </Form.Label>
        {tooltip && (
            <OverlayTrigger placement="top" overlay={<Tooltip id={`si-tip-${htmlFor}`}>{tooltip}</Tooltip>}>
                <button
                    type="button"
                    className="si-help-btn"
                    aria-label={tooltip}
                >
                    <HelpCircle size={15} aria-hidden />
                </button>
            </OverlayTrigger>
        )}
    </div>
);

const StandardImportDropzone = ({
    file,
    onFile,
    disabled,
    inputId,
    label,
    tooltip,
    hint,
    formatSelectedName,
}) => {
    const inputRef = useRef(null);
    const [dragOver, setDragOver] = useState(false);

    const pickFile = useCallback((picked) => {
        if (!picked || disabled) return;
        onFile(picked);
    }, [disabled, onFile]);

    const onDrop = (e) => {
        e.preventDefault();
        setDragOver(false);
        pickFile(e.dataTransfer.files?.[0]);
    };

    return (
        <Form.Group>
            <LabelWithTooltip htmlFor={inputId} label={label} tooltip={tooltip} required />
            <div
                className={`si-dropzone ${dragOver ? 'si-dropzone--active' : ''} ${file ? 'si-dropzone--filled' : ''}`}
                onDragOver={(e) => { e.preventDefault(); if (!disabled) setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={onDrop}
                onClick={() => !disabled && inputRef.current?.click()}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        if (!disabled) inputRef.current?.click();
                    }
                }}
                role="button"
                tabIndex={disabled ? -1 : 0}
                aria-disabled={disabled}
                aria-label={label}
            >
                <input
                    ref={inputRef}
                    id={inputId}
                    type="file"
                    className="si-dropzone__input"
                    accept=".xlsx,.xls,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                    disabled={disabled}
                    onChange={(e) => pickFile(e.target.files?.[0])}
                />
                <Upload size={22} className="si-dropzone__icon" aria-hidden />
                <span className="si-dropzone__title">{hint}</span>
                {file && (
                    <span className="si-dropzone__file" role="status">
                        <FileSpreadsheet size={16} aria-hidden />
                        {formatSelectedName(file.name)}
                        <button
                            type="button"
                            className="si-dropzone__clear"
                            onClick={(e) => {
                                e.stopPropagation();
                                onFile(null);
                                if (inputRef.current) inputRef.current.value = '';
                            }}
                            aria-label="Retirer le fichier"
                        >
                            <X size={14} aria-hidden />
                        </button>
                    </span>
                )}
            </div>
        </Form.Group>
    );
};

const formatSubcoverageWindows = (windows = []) => {
    if (!Array.isArray(windows) || windows.length === 0) return '—';
    return windows.map((window) => {
        const start = window.sub_coverage_start_date || window.subCoverageStartDate || '—';
        const end = window.sub_coverage_end_date || window.subCoverageEndDate || '—';
        const subId = window.sub_coverage_id || window.subCoverageId;
        const idPart = subId ? ` (${subId})` : '';
        return `${start} → ${end}${idPart}`;
    }).join(' ; ');
};

const StandardImportResult = ({
    stats,
    detail,
    dryRun,
    message,
    isFailure,
    missingCompanyName,
    partnerNotFound,
    standardScopeRejected,
    standardScopeRejections,
    activityNotFound,
    hasValidationErrs,
    validationErrors,
    hasPartnerImportErrs,
    partnerImportErrors,
    hasPartnerErrs,
    partnerErrors,
    hasSkipped,
    skippedReservations,
    activityTypeSameDay,
    activityTypeRequiresExcelTimes,
    unknownCompanyNames,
    standardScopeCompanyNames,
    unknownActivities,
    subscriptionEmails,
    t,
    onClear,
}) => {
    const [showDetail, setShowDetail] = useState(false);
    if (!stats) return null;

    const cards = [
        { key: 'policies', value: stats.policiesCreated, Icon: FileText, tone: 'primary' },
        { key: 'duplicates', value: stats.duplicates, Icon: Copy, tone: 'warning' },
        { key: 'companyErrors', value: stats.partnerErrors, Icon: AlertTriangle, tone: 'danger' },
        { key: 'activityErrors', value: stats.activityErrors, Icon: AlertTriangle, tone: 'danger' },
        { key: 'validationErrors', value: stats.validationErrors, Icon: AlertTriangle, tone: 'danger' },
        { key: 'processorErrors', value: stats.processorErrors, Icon: AlertTriangle, tone: 'danger' },
        { key: 'partnerDocumentErrors', value: stats.partnerDocumentErrors, Icon: AlertTriangle, tone: 'danger' },
        { key: 'documents', value: stats.documentsGenerated, Icon: FileSpreadsheet, tone: 'success' },
        { key: 'emailsCreated', value: stats.emailsCreated, Icon: Mail, tone: 'primary' },
        { key: 'emailsSent', value: stats.emailsSent, Icon: Mail, tone: 'success' },
        { key: 'emailsSkipped', value: stats.emailsSkipped, Icon: Mail, tone: 'warning' },
        { key: 'emailsFailed', value: stats.emailsFailed, Icon: Mail, tone: 'danger' },
    ];

    const skippedEmails = Array.isArray(subscriptionEmails?.skipped) ? subscriptionEmails.skipped : [];
    const sentEmails = Array.isArray(subscriptionEmails?.sent) ? subscriptionEmails.sent : [];
    const failedEmails = [
        ...(Array.isArray(subscriptionEmails?.failed) ? subscriptionEmails.failed : []),
        ...(Array.isArray(subscriptionEmails?.email_failures) ? subscriptionEmails.email_failures : []),
    ];
    const hasSubscriptionBlock = Boolean(subscriptionEmails);
    const formatEmailReason = (reason) => formatSubscriptionEmailReason(reason, getEmailListItemLabel);

    const validationHints = [];
    if (activityTypeRequiresExcelTimes) {
        validationHints.push(t(`${PAGE_PREFIX}.standard.result.activityTypeRequiresExcelTimesHint`));
    }
    if (activityTypeSameDay) {
        validationHints.push(t(`${PAGE_PREFIX}.standard.result.activityTypeSameDayHint`));
    }
    if (validationHints.length === 0 && hasValidationErrs) {
        validationHints.push(t(`${PAGE_PREFIX}.standard.result.validationErrorsHint`));
    }

    const resultAlertVariant = (isFailure || hasPartnerErrs)
        ? 'danger'
        : (hasSkipped && stats.policiesCreated === 0 ? 'warning' : 'success');
    const resultAlertTitle = (isFailure || hasPartnerErrs)
        ? (hasPartnerErrs && !hasPartnerImportErrs && !hasValidationErrs
            ? t(`${PAGE_PREFIX}.standard.result.importFinishedWithPartnerErrors`)
            : t(`${PAGE_PREFIX}.standard.result.importFinishedWithErrors`))
        : (hasSkipped && stats.policiesCreated === 0
            ? t(`${PAGE_PREFIX}.standard.result.importFinishedWithSkips`)
            : t(`${PAGE_PREFIX}.standard.result.importFinishedSuccess`));

    return (
        <Card className="si-card si-result-card mb-4">
            <Card.Body>
                <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
                    <h3 className="si-card__title mb-0">{t(`${PAGE_PREFIX}.standard.resultSectionTitle`)}</h3>
                    {dryRun && (
                        <Badge bg="info" className="si-badge">{t(`${PAGE_PREFIX}.standard.result.dryRunBanner`)}</Badge>
                    )}
                </div>
                {message && (
                    <Alert
                        variant={resultAlertVariant}
                        className="mb-3"
                        role="status"
                    >
                        <strong className="d-block mb-1">
                            {resultAlertTitle}
                        </strong>
                        <span>{message}</span>
                        {hasPartnerErrs && partnerErrors.length > 0 && (
                            <ul className="mb-0 mt-2 ps-3 small">
                                {partnerErrors.map((entry, index) => (
                                    <li key={`partner-err-msg-${entry.partner_id || index}`} className="text-break">
                                        {entry.error || entry.message || entry.reason || '—'}
                                    </li>
                                ))}
                            </ul>
                        )}
                    </Alert>
                )}
                {hasSkipped && (
                    <Alert variant="warning" className="mb-3">
                        <div className="d-flex align-items-start gap-2 mb-2">
                            <Copy size={18} className="flex-shrink-0 mt-1" aria-hidden />
                            <span>{t(`${PAGE_PREFIX}.standard.result.skippedReservationsHint`)}</span>
                        </div>
                        {skippedReservations.length > 0 && (
                            <div className="table-responsive border rounded bg-white mt-2">
                                <table className="table table-sm mb-0 align-middle">
                                    <thead className="table-light">
                                        <tr>
                                            <th>{t(`${PAGE_PREFIX}.standard.result.colReservation`)}</th>
                                            <th>{t(`${PAGE_PREFIX}.standard.result.colMessage`)}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {skippedReservations.map((entry, index) => (
                                            <tr key={`skipped-${entry.reservation_number || index}`}>
                                                <td>
                                                    <code>{entry.reservation_number || '—'}</code>
                                                </td>
                                                <td className="small text-break">
                                                    {entry.message || t(`${PAGE_PREFIX}.standard.result.skippedDefault`)}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </Alert>
                )}
                {missingCompanyName && (
                    <Alert variant="warning" className="mb-3 d-flex align-items-start gap-2">
                        <AlertTriangle size={18} className="flex-shrink-0 mt-1" aria-hidden />
                        <span>{t(`${PAGE_PREFIX}.standard.result.missingCompanyNameHint`)}</span>
                    </Alert>
                )}
                {standardScopeRejected && (
                    <Alert variant="danger" className="mb-3">
                        <div className="d-flex align-items-start gap-2 mb-2">
                            <AlertTriangle size={18} className="flex-shrink-0 mt-1" aria-hidden />
                            <span>{t(`${PAGE_PREFIX}.standard.result.standardScopeHint`)}</span>
                        </div>
                        {standardScopeCompanyNames.length > 0 && (
                            <div className="si-unknown-companies mb-2">
                                <span className="small fw-semibold d-block mb-1">
                                    {t(`${PAGE_PREFIX}.standard.result.unknownCompanyNames`)}
                                </span>
                                <div className="d-flex flex-wrap gap-1">
                                    {standardScopeCompanyNames.map((name) => (
                                        <Badge key={name} bg="danger" className="si-unknown-company-badge">
                                            {name}
                                        </Badge>
                                    ))}
                                </div>
                            </div>
                        )}
                        {standardScopeRejections.length > 0 && (
                            <div className="table-responsive border rounded bg-white mt-2">
                                <table className="table table-sm mb-0 align-middle">
                                    <thead className="table-light">
                                        <tr>
                                            <th>{t(`${PAGE_PREFIX}.standard.result.colRow`)}</th>
                                            <th>{t(`${PAGE_PREFIX}.standard.result.colReservation`)}</th>
                                            <th>{t(`${PAGE_PREFIX}.standard.result.colCompany`)}</th>
                                            <th>{t(`${PAGE_PREFIX}.standard.result.colMessage`)}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {standardScopeRejections.map((entry, index) => (
                                            <tr key={`std-scope-${entry.row || index}-${entry.reservation_number || index}`}>
                                                <td>{entry.row ?? '—'}</td>
                                                <td><code>{entry.reservation_number || entry.reservationNumber || '—'}</code></td>
                                                <td>{entry.company_name || entry.companyName || '—'}</td>
                                                <td className="small text-break">
                                                    {entry.message || entry.reason || '—'}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </Alert>
                )}
                {partnerNotFound && (
                    <Alert variant="danger" className="mb-3">
                        <div className="d-flex align-items-start gap-2 mb-2">
                            <AlertTriangle size={18} className="flex-shrink-0 mt-1" aria-hidden />
                            <span>{t(`${PAGE_PREFIX}.standard.result.partnerNotFoundHint`)}</span>
                        </div>
                        {unknownCompanyNames.length > 0 && (
                            <div className="si-unknown-companies mb-2">
                                <span className="small fw-semibold d-block mb-1">
                                    {t(`${PAGE_PREFIX}.standard.result.unknownCompanyNames`)}
                                </span>
                                <div className="d-flex flex-wrap gap-1">
                                    {unknownCompanyNames.map((name) => (
                                        <Badge key={name} bg="danger" className="si-unknown-company-badge">
                                            {name}
                                        </Badge>
                                    ))}
                                </div>
                            </div>
                        )}
                    </Alert>
                )}
                {activityNotFound && (
                    <Alert variant="danger" className="mb-3">
                        <div className="d-flex align-items-start gap-2 mb-2">
                            <AlertTriangle size={18} className="flex-shrink-0 mt-1" aria-hidden />
                            <span>{t(`${PAGE_PREFIX}.standard.result.activityNotFoundHint`)}</span>
                        </div>
                        {unknownActivities.length > 0 && (
                            <div className="si-unknown-activities">
                                <span className="small fw-semibold d-block mb-1">
                                    {t(`${PAGE_PREFIX}.standard.result.unknownActivityNames`)}
                                </span>
                                <ul className="si-unknown-activities__list small mb-0 ps-3">
                                    {unknownActivities.map((entry) => {
                                        const companyName = entry.company_name || entry.companyName || 'N/A';
                                        const activityName = entry.activity_name || entry.activityName || 'N/A';
                                        return (
                                            <li key={`${companyName}|${activityName}`}>
                                                <strong>{activityName}</strong>
                                                <span className="text-muted"> — {companyName}</span>
                                            </li>
                                        );
                                    })}
                                </ul>
                            </div>
                        )}
                    </Alert>
                )}
                {hasValidationErrs && (
                    <Alert variant="danger" className="mb-3">
                        <div className="d-flex align-items-start gap-2 mb-2">
                            <AlertTriangle size={18} className="flex-shrink-0 mt-1" aria-hidden />
                            <div>
                                {validationHints.map((hint) => (
                                    <span key={hint} className="d-block mb-1">{hint}</span>
                                ))}
                            </div>
                        </div>
                        {validationErrors.length > 0 && (
                            <div className="table-responsive border rounded bg-white mt-2">
                                <table className="table table-sm mb-0 align-middle">
                                    <thead className="table-light">
                                        <tr>
                                            <th>{t(`${PAGE_PREFIX}.standard.result.colRow`)}</th>
                                            <th>{t(`${PAGE_PREFIX}.standard.result.colReservation`)}</th>
                                            <th>{t(`${PAGE_PREFIX}.standard.result.colActivity`)}</th>
                                            <th>{t(`${PAGE_PREFIX}.standard.result.colColumn`)}</th>
                                            <th>{t(`${PAGE_PREFIX}.standard.result.colMissing`)}</th>
                                            <th>{t(`${PAGE_PREFIX}.standard.result.colDates`)}</th>
                                            <th>{t(`${PAGE_PREFIX}.standard.result.colMessage`)}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {validationErrors.map((entry, index) => {
                                            const missing = Array.isArray(entry.missing_columns)
                                                ? entry.missing_columns
                                                : (Array.isArray(entry.missingColumns) ? entry.missingColumns : []);
                                            return (
                                                <tr key={`val-err-${entry.row || index}-${entry.reservation_number || index}`}>
                                                    <td>{entry.row ?? '—'}</td>
                                                    <td>
                                                        <code>
                                                            {entry.reservation_number || entry.reservationNumber || '—'}
                                                        </code>
                                                    </td>
                                                    <td className="small">
                                                        {entry.activity_name || entry.activityName || '—'}
                                                        {(entry.activity_type || entry.activityType) && (
                                                            <span className="text-muted d-block">
                                                                {entry.activity_type || entry.activityType}
                                                            </span>
                                                        )}
                                                    </td>
                                                    <td><code>{entry.column || '—'}</code></td>
                                                    <td className="small">
                                                        {missing.length > 0
                                                            ? missing.map((col) => (
                                                                <Badge key={col} bg="danger" className="me-1 mb-1">
                                                                    {col}
                                                                </Badge>
                                                            ))
                                                            : '—'}
                                                    </td>
                                                    <td className="small text-nowrap">
                                                        {(entry.activity_start_date || entry.activityStartDate || '—')}
                                                        {' → '}
                                                        {(entry.activity_end_date || entry.activityEndDate || '—')}
                                                    </td>
                                                    <td className="small text-break">
                                                        {entry.message || entry.reason || '—'}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </Alert>
                )}
                {hasPartnerImportErrs && (
                    <Alert variant="danger" className="mb-3">
                        <div className="d-flex align-items-start gap-2 mb-2">
                            <AlertTriangle size={18} className="flex-shrink-0 mt-1" aria-hidden />
                            <span>{t(`${PAGE_PREFIX}.standard.result.partnerImportErrorsHint`)}</span>
                        </div>
                        {partnerImportErrors.length > 0 && (
                            <div className="table-responsive border rounded bg-white mt-2">
                                <table className="table table-sm mb-0 align-middle">
                                    <thead className="table-light">
                                        <tr>
                                            <th>{t(`${PAGE_PREFIX}.standard.result.colReservation`)}</th>
                                            <th>{t(`${PAGE_PREFIX}.standard.result.colActivity`)}</th>
                                            <th>{t(`${PAGE_PREFIX}.standard.result.colDates`)}</th>
                                            <th>{t(`${PAGE_PREFIX}.standard.result.colSubcoverageWindows`)}</th>
                                            <th>{t(`${PAGE_PREFIX}.standard.result.colMessage`)}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {partnerImportErrors.map((entry, index) => (
                                            <tr key={`partner-err-${entry.reservation_number || index}`}>
                                                <td>
                                                    <code>{entry.reservation_number || entry.reservationNumber || '—'}</code>
                                                    {(entry.partner_type || entry.partnerType) && (
                                                        <span className="text-muted d-block small">
                                                            {entry.partner_type || entry.partnerType}
                                                        </span>
                                                    )}
                                                </td>
                                                <td className="small">
                                                    {entry.activity_name || entry.activityName || '—'}
                                                    {(entry.activity_type || entry.activityType) && (
                                                        <span className="text-muted d-block">
                                                            {entry.activity_type || entry.activityType}
                                                        </span>
                                                    )}
                                                    {(entry.reference_id || entry.referenceId) && (
                                                        <span className="text-muted d-block font-monospace">
                                                            {entry.reference_id || entry.referenceId}
                                                        </span>
                                                    )}
                                                </td>
                                                <td className="small text-nowrap">
                                                    {(entry.activity_start_date || entry.activityStartDate || '—')}
                                                    {' → '}
                                                    {(entry.activity_end_date || entry.activityEndDate || '—')}
                                                </td>
                                                <td className="small text-break">
                                                    {formatSubcoverageWindows(entry.available_subcoverage_windows)}
                                                </td>
                                                <td className="small text-break">
                                                    {entry.error || entry.message || entry.reason || '—'}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </Alert>
                )}
                {hasPartnerErrs && (
                    <Alert variant="danger" className="mb-3">
                        <div className="d-flex align-items-start gap-2 mb-2">
                            <AlertTriangle size={18} className="flex-shrink-0 mt-1" aria-hidden />
                            <span>{t(`${PAGE_PREFIX}.standard.result.partnerErrorsHint`)}</span>
                        </div>
                        {partnerErrors.length > 0 && (
                            <div className="table-responsive border rounded bg-white mt-2">
                                <table className="table table-sm mb-0 align-middle">
                                    <thead className="table-light">
                                        <tr>
                                            <th>{t(`${PAGE_PREFIX}.standard.result.colPartnerId`)}</th>
                                            <th>{t(`${PAGE_PREFIX}.standard.result.colPartnerType`)}</th>
                                            <th>{t(`${PAGE_PREFIX}.standard.result.colMessage`)}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {partnerErrors.map((entry, index) => (
                                            <tr key={`partner-doc-err-${entry.partner_id || index}`}>
                                                <td className="small font-monospace text-break">
                                                    {entry.partner_id || entry.partnerId || '—'}
                                                </td>
                                                <td className="small">
                                                    {entry.partner_type || entry.partnerType || '—'}
                                                </td>
                                                <td className="small text-break">
                                                    {entry.error || entry.message || entry.reason || '—'}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </Alert>
                )}
                {hasSubscriptionBlock && (
                    <Alert
                        variant={failedEmails.length > 0 ? 'danger' : (skippedEmails.length > 0 ? 'warning' : 'info')}
                        className="mb-3"
                    >
                        <div className="d-flex align-items-start gap-2 mb-2">
                            <Mail size={18} className="flex-shrink-0 mt-1" aria-hidden />
                            <div>
                                <strong className="d-block mb-1">
                                    {t(`${PAGE_PREFIX}.standard.result.subscriptionEmailsTitle`)}
                                </strong>
                                <span>{t(`${PAGE_PREFIX}.standard.result.subscriptionEmailsHint`)}</span>
                                {subscriptionEmails.copy_email && (
                                    <div className="small mt-1">
                                        {t(`${PAGE_PREFIX}.standard.result.subscriptionEmailsCopyEmail`)}
                                        {': '}
                                        <code>{subscriptionEmails.copy_email}</code>
                                    </div>
                                )}
                            </div>
                        </div>
                        {skippedEmails.length > 0 && (
                            <div className="table-responsive border rounded bg-white mt-2">
                                <table className="table table-sm mb-0 align-middle">
                                    <thead className="table-light">
                                        <tr>
                                            <th>{t(`${PAGE_PREFIX}.standard.result.colPolicyCode`)}</th>
                                            <th>{t(`${PAGE_PREFIX}.standard.result.colMessage`)}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {skippedEmails.map((entry, index) => (
                                            <tr key={`sub-email-skip-${entry.policy_code || index}`}>
                                                <td><code>{entry.policy_code || entry.policyCode || '—'}</code></td>
                                                <td className="small text-break">
                                                    {formatEmailReason(entry.reason || entry.message)}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                        {sentEmails.length > 0 && (
                            <div className="table-responsive border rounded bg-white mt-2">
                                <table className="table table-sm mb-0 align-middle">
                                    <thead className="table-light">
                                        <tr>
                                            <th>{t(`${PAGE_PREFIX}.standard.result.colPolicyCode`)}</th>
                                            <th>{t(`${PAGE_PREFIX}.standard.result.colMessage`)}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {sentEmails.map((entry, index) => (
                                            <tr key={`sub-email-sent-${entry.policy_code || index}`}>
                                                <td><code>{entry.policy_code || entry.policyCode || (typeof entry === 'string' ? entry : '—')}</code></td>
                                                <td className="small text-break">
                                                    {typeof entry === 'string'
                                                        ? entry
                                                        : (entry.email || entry.client_email || entry.message || '—')}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                        {failedEmails.length > 0 && (
                            <div className="table-responsive border rounded bg-white mt-2">
                                <table className="table table-sm mb-0 align-middle">
                                    <thead className="table-light">
                                        <tr>
                                            <th>{t(`${PAGE_PREFIX}.standard.result.colPolicyCode`)}</th>
                                            <th>{t(`${PAGE_PREFIX}.standard.result.colMessage`)}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {failedEmails.map((entry, index) => (
                                            <tr key={`sub-email-fail-${entry.policy_code || index}`}>
                                                <td><code>{entry.policy_code || entry.policyCode || '—'}</code></td>
                                                <td className="small text-break">
                                                    {formatEmailReason(entry.reason || entry.error || entry.message)}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </Alert>
                )}
                <div className="si-stats-row mb-3">
                    {cards.map(({ key, value, Icon, tone }) => (
                        <div key={key} className={`si-stat si-stat--${tone}`}>
                            <Icon size={20} aria-hidden />
                            <span className="si-stat__value">{value}</span>
                            <span className="si-stat__label">{t(`${PAGE_PREFIX}.standard.stats.${key}`)}</span>
                        </div>
                    ))}
                </div>
                {detail && (
                    <>
                        <Button
                            variant="link"
                            className="si-detail-toggle p-0"
                            onClick={() => setShowDetail((v) => !v)}
                            aria-expanded={showDetail}
                        >
                            {showDetail
                                ? t(`${PAGE_PREFIX}.standard.hideDetailReport`)
                                : t(`${PAGE_PREFIX}.standard.viewDetailReport`)}
                        </Button>
                        {showDetail && (
                            <pre className="si-detail-pre" role="log">{detail}</pre>
                        )}
                    </>
                )}
                <div className="text-end mt-2">
                    <Button variant="outline-secondary" size="sm" onClick={onClear}>
                        {t(`${PAGE_PREFIX}.standard.clearResult`)}
                    </Button>
                </div>
            </Card.Body>
        </Card>
    );
};

const StandardImportPanel = ({ t }) => {
    const std = `${PAGE_PREFIX}.standard`;
    const [importFile, setImportFile] = useState(null);
    const [importLoading, setImportLoading] = useState(false);
    const [importError, setImportError] = useState(null);
    const [importResponse, setImportResponse] = useState(() => {
        try {
            const raw = localStorage.getItem(STORAGE_KEY);
            return raw ? JSON.parse(raw) : null;
        } catch {
            return null;
        }
    });
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [showDocModal, setShowDocModal] = useState(false);
    const [templateDownloading, setTemplateDownloading] = useState(false);

    const handleDownloadTemplate = async () => {
        if (templateDownloading) return;
        setTemplateDownloading(true);
        setImportError(null);
        try {
            await downloadStandardImportTemplate();
        } catch (err) {
            setImportError(handleApiError(err, t(`${std}.templateDownloadError`)));
        } finally {
            setTemplateDownloading(false);
        }
    };

    const getResultLabels = () => ({
        dryRunBanner: t(`${std}.result.dryRunBanner`),
        code: t(`${std}.result.code`),
        totalRows: t(`${std}.result.totalRows`),
        rowsProcessed: t(`${std}.result.rowsProcessed`),
        rowsSkipped: t(`${std}.result.rowsSkipped`),
        rowsFailed: t(`${std}.result.rowsFailed`),
        policiesCreated: t(`${std}.result.policiesCreated`),
        documentsGenerated: t(`${std}.result.documentsGenerated`),
        rejectedCompanyMismatch: t(`${std}.result.rejectedCompanyMismatch`),
        rejectedActivityNotFound: t(`${std}.result.rejectedActivityNotFound`),
        detectedFormat: t(`${std}.result.detectedFormat`),
        successCount: t(`${std}.result.successCount`),
        errorCount: t(`${std}.result.errorCount`),
        createdPolicies: t(`${std}.result.createdPolicies`),
        skippedReservations: t(`${std}.result.skippedReservations`),
        skippedDefault: t(`${std}.result.skippedDefault`),
        errorsTitle: t(`${std}.result.errorsTitle`),
        validationErrorsTitle: t(`${std}.result.validationErrorsTitle`),
        partnerImportErrorsTitle: t(`${std}.result.partnerImportErrorsTitle`),
        partnerImportErrors: t(`${std}.result.partnerImportErrorsCount`),
        partnerErrorsTitle: t(`${std}.result.partnerErrorsTitle`),
        partnerErrorsCount: t(`${std}.result.partnerErrorsCount`),
        subcoverageWindow: t(`${std}.result.subcoverageWindow`),
        unknownError: t(`${std}.result.unknownError`),
        detailsCount: t(`${std}.result.detailsCount`),
        emptyResult: t(`${std}.result.emptyResult`),
        subscriptionEmailsTitle: t(`${std}.result.subscriptionEmailsTitle`),
        subscriptionEmailsCreated: t(`${std}.result.subscriptionEmailsCreated`),
        subscriptionEmailsSent: t(`${std}.result.subscriptionEmailsSent`),
        subscriptionEmailsSkipped: t(`${std}.result.subscriptionEmailsSkipped`),
        subscriptionEmailsFailed: t(`${std}.result.subscriptionEmailsFailed`),
        subscriptionEmailsCopyEmail: t(`${std}.result.subscriptionEmailsCopyEmail`),
    });

    const persistResult = (response) => {
        setImportResponse(response);
        try {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(response));
        } catch {
            // ignore
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (!importFile) {
            setImportError(t(`${std}.validation.noFile`));
            return;
        }
        setShowConfirmModal(true);
    };

    const handleConfirm = async () => {
        if (!importFile) return;
        setShowConfirmModal(false);
        setImportLoading(true);
        setImportError(null);

        try {
            const response = await importTripsPoliciesStandard(importFile, {
                dry_run: false,
                strict_company_check: true,
            });
            const detailError = getResponseDetailError(response);
            if (detailError) {
                setImportError(detailError);
                return;
            }
            persistResult(response);
            setImportFile(null);
        } catch (err) {
            setImportError(handleApiError(err, t(`${std}.error`)));
        } finally {
            setImportLoading(false);
        }
    };

    const handleReset = () => {
        setImportFile(null);
        setImportError(null);
        setShowConfirmModal(false);
    };

    const handleClearResult = () => {
        setImportResponse(null);
        localStorage.removeItem(STORAGE_KEY);
    };

    const resultStats = importResponse ? parseStandardImportStats(importResponse) : null;
    const resultDetail = importResponse
        ? buildTripsPoliciesStandardResultMessage(importResponse, getResultLabels())
        : null;
    const unknownCompanyNames = importResponse
        ? getRejectedUnknownCompanyNames(importResponse)
        : [];
    const standardScopeCompanyNames = importResponse
        ? getRejectedStandardScopeCompanyNames(importResponse)
        : [];
    const standardScopeRejections = importResponse
        ? getStandardScopeRejections(importResponse)
        : [];
    const unknownActivities = importResponse
        ? getRejectedUnknownActivities(importResponse)
        : [];
    const validationErrors = importResponse
        ? getStandardValidationErrors(importResponse)
        : [];
    const partnerImportErrors = importResponse
        ? getPartnerImportErrors(importResponse)
        : [];
    const partnerErrors = importResponse
        ? getPartnerErrors(importResponse)
        : [];
    const skippedReservations = importResponse
        ? getSkippedReservations(importResponse)
        : [];
    const subscriptionEmails = importResponse
        ? getSubscriptionEmails(importResponse)
        : null;
    const importIsFailure = importResponse
        ? isTripsPoliciesStandardFailure(importResponse)
        : false;

    return (
        <div className="standard-import-panel">
            <header className="si-header mb-4">
                <div className="si-header__text">
                    <h2 className="si-header__title">{t(`${std}.heroTitle`)}</h2>
                    <p className="si-header__subtitle text-muted mb-0">{t(`${std}.oneLineDescription`)}</p>
                </div>
                <div className="si-header__actions">
                    <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={handleDownloadTemplate}
                        disabled={templateDownloading || importLoading}
                        className="d-inline-flex align-items-center gap-2"
                    >
                        {templateDownloading ? (
                            <Spinner size="sm" animation="border" aria-hidden />
                        ) : (
                            <FiDownload aria-hidden />
                        )}
                        {t(`${PAGE_PREFIX}.downloadTemplate`)}
                    </Button>
                    <Button
                        variant="outline-secondary"
                        size="sm"
                        onClick={() => setShowDocModal(true)}
                        className="d-inline-flex align-items-center gap-2"
                    >
                        <FiBookOpen aria-hidden />
                        {t(`${PAGE_PREFIX}.viewDocumentation`)}
                    </Button>
                </div>
            </header>

            {importLoading && (
                <Alert variant="info" className="si-alert d-flex align-items-center gap-2 mb-4" role="status">
                    <Spinner size="sm" animation="border" aria-hidden />
                    <span>{t(`${PAGE_PREFIX}.loading`)}</span>
                </Alert>
            )}

            {importError && (
                <Alert variant="danger" dismissible onClose={() => setImportError(null)} className="si-alert mb-4" role="alert">
                    {importError}
                </Alert>
            )}

            <Form onSubmit={handleSubmit}>
                <Card className="si-card mb-4">
                    <Card.Body>
                        <h3 className="si-card__title">{t(`${std}.sectionColumns`)}</h3>
                        <div className="si-columns" role="list" aria-label={t(`${std}.columnsTitle`)}>
                            {STANDARD_REQUIRED_COLUMNS.map((column) => (
                                <Badge key={column} bg="light" text="dark" className="si-column-tag" role="listitem">
                                    {column}
                                </Badge>
                            ))}
                        </div>
                        <p className="si-columns-more text-muted mb-2 mt-2">{t(`${std}.otherColumns`)}</p>
                        <p className="small fw-semibold mb-1">{t(`${std}.optionalTimeColumnsTitle`)}</p>
                        <div className="si-columns" role="list" aria-label={t(`${std}.optionalTimeColumnsTitle`)}>
                            {STANDARD_IMPORT_OPTIONAL_TIME_COLUMNS.map((column) => (
                                <Badge key={column} bg="warning" text="dark" className="si-column-tag" role="listitem">
                                    {column}
                                </Badge>
                            ))}
                        </div>
                        <p className="si-columns-more text-muted mb-0 mt-2">{t(`${std}.optionalTimeColumnsHint`)}</p>
                    </Card.Body>
                </Card>

                <Card className="si-card mb-4">
                    <Card.Body>
                        <h3 className="si-card__title">{t(`${std}.cardConfig`)}</h3>
                        <StandardImportDropzone
                            file={importFile}
                            onFile={(f) => { setImportFile(f); setImportError(null); }}
                            disabled={importLoading}
                            inputId="standard-import-file"
                            label={t(`${std}.fileLabel`)}
                            tooltip={t(`${std}.fileTooltip`)}
                            hint={t(`${std}.dropzoneHint`)}
                            formatSelectedName={(name) => t(`${std}.fileSelected`, { name })}
                        />
                    </Card.Body>
                </Card>

                {resultStats && (
                    <StandardImportResult
                        stats={resultStats}
                        detail={resultDetail}
                        message={importResponse?.message}
                        isFailure={importIsFailure}
                        dryRun={importResponse?.dry_run === true}
                        missingCompanyName={hasMissingCompanyNameRejections(importResponse)}
                        partnerNotFound={hasPartnerNotFoundRejections(importResponse)}
                        standardScopeRejected={hasStandardScopeRejections(importResponse)}
                        standardScopeRejections={standardScopeRejections}
                        activityNotFound={hasActivityNotFoundRejections(importResponse)}
                        hasValidationErrs={hasValidationErrors(importResponse)}
                        validationErrors={validationErrors}
                        hasPartnerImportErrs={hasPartnerImportErrors(importResponse)}
                        partnerImportErrors={partnerImportErrors}
                        hasPartnerErrs={hasPartnerErrors(importResponse)}
                        partnerErrors={partnerErrors}
                        hasSkipped={hasSkippedReservations(importResponse)}
                        skippedReservations={skippedReservations}
                        activityTypeSameDay={hasActivityTypeSameDayValidationErrors(importResponse)}
                        activityTypeRequiresExcelTimes={hasActivityTypeRequiresExcelTimesValidationErrors(importResponse)}
                        unknownCompanyNames={unknownCompanyNames}
                        standardScopeCompanyNames={standardScopeCompanyNames}
                        unknownActivities={unknownActivities}
                        subscriptionEmails={subscriptionEmails}
                        t={t}
                        onClear={handleClearResult}
                    />
                )}

                <Accordion className="si-help mb-4">
                    <Accordion.Item eventKey="0">
                        <Accordion.Header>{t(`${std}.helpAccordionTitle`)}</Accordion.Header>
                        <Accordion.Body>
                            <ul className="si-help-list mb-0">
                                {[1, 2, 3].map((n) => (
                                    <li key={n}>{t(`${std}.helpItem${n}`)}</li>
                                ))}
                            </ul>
                        </Accordion.Body>
                    </Accordion.Item>
                </Accordion>

                <p className="mb-4">
                    <button
                        type="button"
                        className="si-api-link"
                        onClick={() => setShowDocModal(true)}
                    >
                        {t(`${std}.apiDetailsLink`)}
                    </button>
                </p>

                <div className="si-actions">
                    <Button variant="outline-secondary" onClick={handleReset} disabled={importLoading}>
                        {t('common.reset')}
                    </Button>
                    <Button
                        type="submit"
                        variant="success"
                        disabled={importLoading || !importFile}
                        aria-busy={importLoading}
                        className="d-inline-flex align-items-center gap-2"
                    >
                        {importLoading ? (
                            <>
                                <Spinner size="sm" animation="border" aria-hidden />
                                {t(`${std}.importing`)}
                            </>
                        ) : (
                            <>
                                <Upload size={16} aria-hidden />
                                {t(`${std}.importButton`)}
                            </>
                        )}
                    </Button>
                </div>
            </Form>

            <Modal show={showConfirmModal} onHide={() => setShowConfirmModal(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>{t(`${std}.confirmTitle`)}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p className="mb-2">{t(`${std}.confirmMessage`)}</p>
                    {importFile && (
                        <p className="mb-2 small text-muted">
                            <strong>{t(`${std}.filterFilename`)} :</strong> {importFile.name}
                        </p>
                    )}
                    <Alert variant="warning" className="mb-0">{t(`${std}.confirmWarning`)}</Alert>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="outline-secondary" onClick={() => setShowConfirmModal(false)}>
                        {t('common.cancel')}
                    </Button>
                    <Button variant="success" onClick={handleConfirm} disabled={importLoading}>
                        {t(`${std}.confirmSend`)}
                    </Button>
                </Modal.Footer>
            </Modal>

            <Modal show={showDocModal} onHide={() => setShowDocModal(false)} centered size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>{t(`${std}.docModalTitle`)}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p className="mb-2">{t(`${std}.docModalIntro`)}</p>
                    <div className="si-endpoint mb-4">{t(`${std}.endpointValue`)}</div>
                    <p className="mb-2">{t(`${std}.descriptionText`)}</p>
                    <h3 className="h6 fw-semibold">{t(`${std}.docModalParams`)}</h3>
                    <ul className="small text-muted mb-4">
                        <li>{t(`${std}.docParamDryRun`)}</li>
                        <li>{t(`${std}.docParamSheet`)}</li>
                        <li>{t(`${std}.docParamStrict`)}</li>
                    </ul>
                    <p className="mb-0 small text-muted">{t(`${std}.docModalRoles`)}</p>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="primary" onClick={() => setShowDocModal(false)}>
                        {t('common.confirm')}
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default StandardImportPanel;
